import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HandHeart, ArrowLeft, Lock, Globe, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  novo: { label: "Novo", cls: "bg-[hsl(var(--sky))]/15 text-[hsl(var(--sky))]" },
  orando: { label: "Em oração", cls: "bg-accent/15 text-accent" },
  respondido: { label: "Respondido", cls: "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))]" },
};

export default function PrayerWall() {
  const [groups, setGroups] = useState([]);
  const [wall, setWall] = useState([]);
  const [prayed, setPrayed] = useState({});
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [request, setRequest] = useState("");
  const [visibility, setVisibility] = useState("publico");
  const [done, setDone] = useState(false);

  const loadWall = useCallback(() => {
    api.get("/prayers/public").then((r) => setWall(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/groups", { params: { active_only: true } }).then((r) => setGroups(r.data)).catch(() => {});
    loadWall();
  }, [loadWall]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !request.trim()) return toast.error("Preencha nome e pedido.");
    try {
      await api.post("/prayers", { name, group_id: groupId || null, request, visibility });
      setName(""); setRequest(""); setGroupId(""); setVisibility("publico");
      setDone(true);
      loadWall();
      setTimeout(() => setDone(false), 4000);
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    }
  };

  const pray = async (id) => {
    if (prayed[id]) return;
    try {
      const { data } = await api.post(`/prayers/${id}/pray`);
      setPrayed((p) => ({ ...p, [id]: true }));
      setWall((w) => w.map((x) => (x.id === id ? { ...x, prayer_count: data.prayer_count } : x)));
      toast.success("Amém! Registramos sua oração 🙏");
    } catch {
      toast.error("Erro ao registrar oração");
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="sticky top-0 z-40 border-b border-border bg-card/70 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo compact />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Início
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-10 animate-fade-up">
          <span className="inline-flex h-14 w-14 rounded-2xl bg-accent/15 text-accent items-center justify-center mb-4">
            <HandHeart className="h-7 w-7" />
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Mural de Oração</h1>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            "Orai uns pelos outros." Compartilhe seu pedido e interceda pelos irmãos dos Pequenos Grupos.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <Card className="p-6 lg:col-span-2 h-fit lg:sticky lg:top-24" data-testid="prayer-form-card">
            <h2 className="font-display text-xl font-semibold mb-4">Enviar pedido</h2>
            {done && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--emerald))]/10 text-[hsl(var(--emerald))] px-3 py-2 text-sm" data-testid="prayer-success-message">
                <CheckCircle2 className="h-4 w-4" /> Pedido enviado. Estamos orando por você!
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Seu nome</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="prayer-name-input" />
              </div>
              <div>
                <Label>Seu grupo (opcional)</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="mt-1.5" data-testid="prayer-group-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pedido de oração</Label>
                <Textarea value={request} onChange={(e) => setRequest(e.target.value)} required rows={4} className="mt-1.5" data-testid="prayer-request-input" />
              </div>
              <div>
                <Label>Visibilidade</Label>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger className="mt-1.5" data-testid="prayer-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publico">Público — aparece no mural</SelectItem>
                    <SelectItem value="confidencial">Confidencial — só a liderança vê</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                  {visibility === "publico" ? <><Globe className="h-3 w-3" /> Visível a todos no mural</> : <><Lock className="h-3 w-3" /> Apenas líderes e coordenação verão</>}
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="prayer-submit-button">Enviar pedido</Button>
            </form>
          </Card>

          {/* Wall */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="font-display text-xl font-semibold">Pedidos da comunidade</h2>
            {wall.length === 0 && <p className="text-muted-foreground text-sm">Ainda não há pedidos públicos. Seja o primeiro a compartilhar.</p>}
            {wall.map((p) => {
              const sm = STATUS_META[p.status] || STATUS_META.novo;
              return (
                <Card key={p.id} className="p-5" data-testid="prayer-wall-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{p.name}</span>
                        {p.group_name && <Badge variant="secondary" className="text-xs">{p.group_name}</Badge>}
                        <Badge className={`${sm.cls} border-none text-xs`}>{sm.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.request}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Heart className="h-3.5 w-3.5 text-accent" /> {p.prayer_count} orando
                    </span>
                    <Button
                      size="sm"
                      variant={prayed[p.id] ? "secondary" : "outline"}
                      onClick={() => pray(p.id)}
                      disabled={prayed[p.id]}
                      className="gap-1.5"
                      data-testid={`pray-button-${p.id}`}
                    >
                      <HandHeart className="h-4 w-4" /> {prayed[p.id] ? "Orando" : "Vou orar"}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
